/**
 * Schul-Statistik (V-16): Wie entwickeln sich meine Noten je Fach, und
 * lerne ich dort, wo ich es brauche?
 */
import { inPunkte, noteAusPunkten, schnittPunkte } from '@/domain/school/grades';
import { minutenVon, tagVon } from '@/domain/school/studyTime';
import { abschnitte, liegtIn, runde1, type Abschnitt, type Zeitraum } from '@/domain/stats/zeitraum';
import type { Grade, LocalDate, Notensystem, StudySession, Subject } from '@/domain/types';

/** Wert im eingestellten System: Punkte oder Note. */
export const imSystem = (punkte: number, system: Notensystem) => runde1(system === 'punkte' ? punkte : noteAusPunkten(punkte));

/**
 * Notenverlauf: je Fach der gewichtete Schnitt aller Noten bis zum Ende
 * jedes Abschnitts (wie er im Zeugnis stuende). Vor der ersten Note `null`.
 */
export function notenVerlauf(noten: Grade[], faecher: Subject[], z: Zeitraum, system: Notensystem): { abschnitte: Abschnitt[]; reihen: { fach: Subject; werte: (number | null)[] }[] } {
  const liste = abschnitte(z);
  const reihen = faecher
    .map((fach) => {
      const eigene = noten.filter((n) => n.subjectId === fach.id);
      return {
        fach,
        werte: liste.map((a) => { const s = schnittPunkte(eigene.filter((n) => n.date <= a.bis)); return s === null ? null : imSystem(s, system); }),
      };
    })
    .filter((r) => r.werte.some((w) => w !== null));
  return { abschnitte: liste, reihen };
}

export interface FachZeile {
  fach: Subject;
  minuten: number;
  noten: number;
  /** Gewichteter Schnitt aller Noten im System; `null` ohne Noten. */
  schnitt: number | null;
  /** Punkte (fuer die Sortierung unabhaengig vom System). */
  punkte: number | null;
}

/** Lernzeit im Zeitraum neben dem Schnitt des Fachs. */
export function lernzeitGegenNote(sitzungen: StudySession[], noten: Grade[], faecher: Subject[], z: Zeitraum, system: Notensystem, jetzt: Date): FachZeile[] {
  return faecher.map((fach) => {
    const eigene = noten.filter((n) => n.subjectId === fach.id);
    const p = schnittPunkte(eigene);
    return {
      fach,
      minuten: Math.round(sitzungen.filter((s) => s.subjectId === fach.id && liegtIn(tagVon(s), z)).reduce((sum, s) => sum + minutenVon(s, jetzt), 0)),
      noten: eigene.length,
      schnitt: p === null ? null : imSystem(p, system),
      punkte: p === null ? null : runde1(p),
    };
  });
}

/** Einzelne Noten eines Fachs in zeitlicher Folge, mit laufendem Schnitt. */
export function fachNoten(noten: Grade[], system: Notensystem): { note: Grade; wert: number; schnitt: number }[] {
  const sortiert = [...noten].sort((a, b) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt));
  return sortiert.map((note, i) => ({ note, wert: imSystem(inPunkte(note), system), schnitt: imSystem(schnittPunkte(sortiert.slice(0, i + 1))!, system) }));
}

/** Beginn des Schuljahres: 1. August. */
export function schuljahrBeginn(heute: LocalDate): LocalDate {
  const j = Number(heute.slice(0, 4));
  return `${Number(heute.slice(5, 7)) >= 8 ? j : j - 1}-08-01`;
}

/** Lernminuten je Abschnitt des Zeitraums (Tag, Woche oder Monat). */
export function lernzeitVerlauf(sitzungen: StudySession[], z: Zeitraum, jetzt: Date) {
  const liste = abschnitte(z);
  const werte = liste.map(() => 0);
  for (const s of sitzungen) {
    const i = liste.findIndex((a) => tagVon(s) >= a.von && tagVon(s) <= a.bis);
    if (i >= 0) werte[i]! += minutenVon(s, jetzt);
  }
  return liste.map((a, i) => ({ ...a, minuten: Math.round(werte[i]!) }));
}
